/**
 * Outreach strategy definitions
 */

import type { OutreachStrategy } from '../types/campaign'

export type StrategyDefinition = {
  readonly id: OutreachStrategy
  readonly name: string
  readonly description: string
  readonly bestFor: string
  readonly example: string
}

export const OUTREACH_STRATEGIES: readonly StrategyDefinition[] = [
  {
    id: 'pain_agitate',
    name: 'Pain & Agitate',
    description: 'Lead with a known pain point the prospect faces, then agitate it to create urgency.',
    bestFor: 'Prospects with clearly identifiable pain points or industry-wide challenges.',
    example: 'Noticed your team is still manually qualifying leads — that must eat 10+ hours/week. We automated that for [Similar Company] and saved their SDRs 60% of prospecting time.',
  },
  {
    id: 'mutual_value',
    name: 'Mutual Value',
    description: 'Offer reciprocal value exchange — give before you ask.',
    bestFor: 'Peers and same-level professionals where a two-way relationship makes sense.',
    example: 'I put together a benchmarking report on outbound conversion rates in SaaS — happy to share it. Would love to compare notes on what\'s working for your team.',
  },
  {
    id: 'social_proof',
    name: 'Social Proof',
    description: 'Reference results achieved with similar companies to build credibility.',
    bestFor: 'Competitive industries where prospects want to know what peers are doing.',
    example: 'We helped 3 Series B fintechs double their outbound pipeline in 90 days. Given [Company]\'s growth stage, the same playbook could work well for you.',
  },
  {
    id: 'curiosity_gap',
    name: 'Curiosity Gap',
    description: 'Open a loop with an intriguing observation that invites a response.',
    bestFor: 'Senior decision-makers who are hard to engage with direct pitches.',
    example: 'I noticed something interesting about how [Company] positions against [Competitor] — it\'s actually a huge untapped advantage. Mind if I share what I found?',
  },
  {
    id: 'direct_expert',
    name: 'Direct Expert',
    description: 'Share an authoritative insight that demonstrates deep domain expertise.',
    bestFor: 'Technical buyers and subject-matter experts who value substance.',
    example: 'After analyzing 50k+ LinkedIn outreach sequences, we found that messages under 300 characters get 2.3x more replies. Your team\'s current approach has room to leverage this.',
  },
  {
    id: 'challenge',
    name: 'Challenge',
    description: 'Provocatively challenge an industry assumption to spark a conversation.',
    bestFor: 'Thought leaders and contrarian thinkers who enjoy intellectual debate.',
    example: 'Most sales teams think more SDR activity = more pipeline. Our data shows the opposite — fewer, better-targeted touches outperform high-volume spray by 3x.',
  },
  {
    id: 'trojan_horse',
    name: 'Trojan Horse',
    description: 'Offer a genuinely useful free resource or audit to start the conversation.',
    bestFor: 'Prospects who are skeptical of outbound and need value upfront.',
    example: 'I built a free LinkedIn profile audit tool — ran yours through it and found 3 quick wins that could boost your profile views 40%. Want me to send the report?',
  },
  {
    id: 'mutual_connection',
    name: 'Mutual Connection',
    description: 'Leverage shared connections, groups, or events to establish rapport.',
    bestFor: 'Prospects with visible shared connections or group memberships.',
    example: 'Saw we\'re both in the [Group Name] community — [Mutual Connection]\'s post last week about AI in sales really resonated. Would love to continue that conversation.',
  },
] as const

export const ALL_STRATEGIES: readonly OutreachStrategy[] = OUTREACH_STRATEGIES.map(s => s.id)
